import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Loader, Typography } from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { getSidePanelHistory } from '../../../../redux/certlist/action';
import { FORCED_CANCELLATION_MESSAGE } from '../../../../redux/certlist/types';
import { SANITIZED_DATE_FORMATS_MAPPING } from '../../../../utils/constant';
import { useInvertedLocalize } from '../../../../utils/localization';
import { getMessageKey } from '../../../../utils/utils';
import { LIST_SP_TAB_HISTORY } from '../constant';

import { FieldSet, HistoryCard } from './CertsListSidePanelBodyCommon';
import CertsListSidePanelBodyError from './CertsListSidePanelBodyError';
import {
  HISTORY_COLORS_MAPPING,
  HISTORY_ICONS_MAPPING,
  VALID_HISTORY_ACTIONS,
} from './constant';
import { invertedMessages } from './messages';

import './CertsListSidePanelBody.css';

function CertsListSidePanelBodyHistory({
  historyData,
  activeTab,
  handleCloseSidePanel,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const l = useInvertedLocalize(invertedMessages);
  const {
    global: { globalDateFormat },
    sidePanel: { sidePanelMetaData },
  } = useSelector((state) => state?.certifications?.list);
  const { messages } = useSelector((state) => state?.user);

  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [history, setHistory] = useState(null);

  const HISTORY_USER_ACTION_LABEL_MAPPING = {
    certificationdiscontinued:
      messages.CERTS_CANCELLED_BY ||
      localize(intl, getMessageKey('CERTS_CANCELLED_BY')),
    canceled:
      messages.CERTS_CANCELLED_BY ||
      localize(intl, getMessageKey('CERTS_CANCELLED_BY')),
    certificationexported:
      messages.CERTS_EXPORTED_BY ||
      localize(intl, getMessageKey('CERTS_EXPORTED_BY')),
    exported:
      messages.CERTS_EXPORTED_BY ||
      localize(intl, getMessageKey('CERTS_EXPORTED_BY')),
    reassignedcertification:
      messages.CERTS_REASSIGNED_FROM ||
      localize(intl, getMessageKey('CERTS_REASSIGNED_FROM')),
    reassigned:
      messages.CERTS_REASSIGNED_FROM ||
      localize(intl, getMessageKey('CERTS_REASSIGNED_FROM')),
    certificationlocked:
      messages.CERTS_COMPLETED_BY ||
      localize(intl, getMessageKey('CERTS_COMPLETED_BY')),
    completed:
      messages.CERTS_COMPLETED_BY ||
      localize(intl, getMessageKey('CERTS_COMPLETED_BY')),
    default:
      messages.CERTS_ACTIONED_BY ||
      localize(intl, getMessageKey('CERTS_ACTIONED_BY')),
  };

  const HISTORY_DATE_ACTION_LABEL_MAPPING = {
    certificationdiscontinued:
      messages.CERTS_CANCELLED_ON ||
      localize(intl, getMessageKey('CERTS_CANCELLED_ON')),
    canceled:
      messages.CERTS_CANCELLED_ON ||
      localize(intl, getMessageKey('CERTS_CANCELLED_ON')),
    certificationexported:
      messages.CERTS_EXPORTED_ON ||
      localize(intl, getMessageKey('CERTS_EXPORTED_ON')),
    exported:
      messages.CERTS_EXPORTED_ON ||
      localize(intl, getMessageKey('CERTS_EXPORTED_ON')),
    reassignedcertification:
      messages.CERTS_REASSIGNED_ON ||
      localize(intl, getMessageKey('CERTS_REASSIGNED_ON')),
    reassigned:
      messages.CERTS_REASSIGNED_ON ||
      localize(intl, getMessageKey('CERTS_REASSIGNED_ON')),
    certificationlocked:
      messages.CERTS_COMPLETED_ON ||
      localize(intl, getMessageKey('CERTS_COMPLETED_ON')),
    completed:
      messages.CERTS_COMPLETED_ON ||
      localize(intl, getMessageKey('CERTS_COMPLETED_ON')),
    default:
      messages.CERTS_ACTIONED_BY ||
      localize(intl, getMessageKey('CERTS_ACTIONED_ON')),
  };

  const callApiForHistory = (certkey) => {
    setIsHistoryLoading(true);
    setIsError(false);

    dispatch(getSidePanelHistory(certkey))
      .then((response) => {
        setHistory(
          response?.data?.filter((historyItem) =>
            VALID_HISTORY_ACTIONS.includes(
              historyItem?.action.replace(/\s+/g, '').toLowerCase()
            )
          )
        );
        setIsHistoryLoading(false);
      })
      .catch((error) => {
        const errorObject = JSON.parse(error.message);

        if (errorObject.error?.message !== FORCED_CANCELLATION_MESSAGE) {
          setIsError(true);
          setIsHistoryLoading(false);
        }
      });
  };

  useEffect(() => {
    setHistory(
      historyData?.filter((historyItem) =>
        VALID_HISTORY_ACTIONS.includes(
          historyItem?.action.replace(/\s+/g, '').toLowerCase()
        )
      )
    );
  }, [historyData]);

  useEffect(() => {
    if (activeTab !== LIST_SP_TAB_HISTORY) {
      setIsHistoryLoading(true);
      setHistory(null);

      return;
    }

    callApiForHistory(sidePanelMetaData?.id);
  }, [activeTab]);

  if (isHistoryLoading) {
    return (
      <Loader
        kind='dots'
        format='inline'
        color='brand'
        className='CertsListSidePanelBodyHistory-loader'
      />
    );
  }

  if (isError) {
    return (
      <CertsListSidePanelBodyError
        tab={LIST_SP_TAB_HISTORY}
        retriggerApisForError={() => callApiForHistory(sidePanelMetaData?.id)}
        handleCloseSidePanel={handleCloseSidePanel}
      />
    );
  }

  if (Array.isArray(history) && history?.length === 0) {
    return (
      <Box tag='div' className='CertsListSidePanelBodyHistory-noDataScreen'>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color='--color-foreground-neutral-bold'
          className='CertsListSidePanelBodyHistory-info'>
          {messages.CERTS_LIST_NO_ACTIONS_MSG ||
            localize(intl, getMessageKey('CERTS_LIST_NO_ACTIONS_MSG'))}
        </Typography>
      </Box>
    );
  }

  return (
    <Box tag='div' className='CertsListSidePanelBodyHistory'>
      <Typography
        kind='body3'
        desktop='body3-d'
        mobile='body3-d'
        color='--color-foreground-neutral-subtle'
        className='CertsListSidePanelBodyHistory-info'>
        {messages.CERTS_LIST_REVIEW_ACTIONS_MSG ||
          localize(intl, getMessageKey('CERTS_LIST_REVIEW_ACTIONS_MSG'))}
      </Typography>
      {history?.map((historyRecords, idx) => {
        const historyAction = historyRecords?.action
          .replace(/\s+/g, '')
          .toLowerCase();
        const isReassignAction = Boolean(
          historyAction === 'reassignedcertification' ||
            historyAction === 'reassigned'
        );

        return (
          <HistoryCard
            title={l(historyRecords?.actionMessage)}
            timelineColor={
              HISTORY_COLORS_MAPPING[historyAction] ||
              HISTORY_COLORS_MAPPING.default
            }
            timelineKind={
              HISTORY_ICONS_MAPPING[historyAction] ||
              HISTORY_ICONS_MAPPING.default
            }
            isTailVisible={idx !== history.length - 1}>
            {isReassignAction ? (
              <div className='CertsListSidePanelBodyHistory-fieldsetWrapper--isVertical'>
                <FieldSet
                  title={
                    messages.CERTS_REASSIGNED_FROM ||
                    localize(intl, getMessageKey('CERTS_REASSIGNED_FROM'))
                  }
                  value={`${historyRecords?.reassignedFrom}`}
                  lineLimitValue={1}
                />
                <FieldSet
                  title={
                    messages.CERTS_REASSIGNED_TO ||
                    localize(intl, getMessageKey('CERTS_REASSIGNED_TO'))
                  }
                  value={`${historyRecords?.reassignedTo}`}
                  lineLimitValue={1}
                />
              </div>
            ) : (
              <FieldSet
                title={
                  HISTORY_USER_ACTION_LABEL_MAPPING[historyAction] ||
                  HISTORY_USER_ACTION_LABEL_MAPPING.default
                }
                value={`${historyRecords?.actionUser}`}
                lineLimitValue={1}
                isGapRequired={false}
              />
            )}
            <FieldSet
              title={
                HISTORY_DATE_ACTION_LABEL_MAPPING[historyAction] ||
                HISTORY_DATE_ACTION_LABEL_MAPPING.default
              }
              value={moment
                .utc(
                  historyRecords?.actionDate,
                  SANITIZED_DATE_FORMATS_MAPPING?.[
                    historyRecords?.actionDateFormat
                  ]
                )
                .local()
                .format(`${globalDateFormat?.toUpperCase()} hh:mm:ss`)}
              isGapRequired={false}
            />
            {historyRecords?.comment?.trim()?.length > 0 && (
              <FieldSet
                title={
                  messages.CERTS_COMMENTS ||
                  localize(intl, getMessageKey('CERTS_COMMENTS'))
                }
                value={`${historyRecords?.comment}`}
                lineLimitValue={2}
                isGapRequired={false}
              />
            )}
          </HistoryCard>
        );
      })}
    </Box>
  );
}

CertsListSidePanelBodyHistory.propTypes = {
  historyData: PropTypes.arrayOf(PropTypes.shape({})),
  activeTab: PropTypes.string,
  handleCloseSidePanel: PropTypes.func,
};

CertsListSidePanelBodyHistory.defaultProps = {
  historyData: null,
  activeTab: '',
  handleCloseSidePanel: () => {},
};

export default CertsListSidePanelBodyHistory;
